import { Section } from '@/components/primitives/Section';
import { Card } from '@/components/ui/card';
import { MapPin, Calculator, User, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export function HowItWorksSection() {
  const steps = [
    {
      number: 1,
      icon: <MapPin className="w-8 h-8" strokeWidth={2.5} />,
      title: 'Drop your pin',
      description: 'Enter your address, confirm on the map. Set your driveway size once—we remember it.',
      color: 'from-brand-500 to-brand-600',
    },
    {
      number: 2,
      icon: <Calculator className="w-8 h-8" strokeWidth={2.5} />,
      title: 'See your price instantly',
      description: 'Full cost breakdown upfront. Add walkways or stairs if needed. Zero surprises.',
      color: 'from-accent-500 to-accent-600',
    },
    {
      number: 3,
      icon: <User className="w-8 h-8" strokeWidth={2.5} />,
      title: 'Get matched with a pro',
      description: 'We find the best local operator with the right equipment. Verified, insured, ready.',
      color: 'from-brand-600 to-brand-700',
    },
    {
      number: 4,
      icon: <CheckCircle className="w-8 h-8" strokeWidth={2.5} />,
      title: 'Track, approve, done',
      description: 'Watch real-time progress. Review before/after photos. Approve work and tip in-app. Tips are 100% operator received.',
      color: 'from-accent-600 to-brand-500',
    },
  ];

  return (
    <Section
      id="how-it-works"
      eyebrow="Dead Simple Process"
      title="From buried in Snow to 'Thanks Snowblowrr!' in 4 easy taps"
      subtitle="No phone tag. No paperwork. No waiting around. Just fast, tracked, photo-verified snow removal that works so good, you don't have 'snowaches' anymore."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16 relative">
        {/* Connecting Line */}
        <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-1 bg-gradient-to-r from-brand-500 via-accent-500 to-brand-500 opacity-20" />
        
        {steps.map((step, index) => (
          <motion.div
            key={step.number}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card 
              variant="outline" 
              className="h-full hover:border-brand-500 hover:shadow-card-hover transition-all duration-300 group relative overflow-hidden"
            >
              {/* Hover gradient effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
              
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <motion.div 
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.color} text-white flex items-center justify-center font-black text-2xl shadow-lg`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    {step.number}
                  </motion.div>
                  <div className={`text-brand-500 group-hover:scale-110 transition-transform duration-300`}>
                    {step.icon}
                  </div>
                </div>
                
                <h3 className="font-black text-xl mb-3 text-neutral-900 group-hover:text-brand-500 transition-colors">
                  {step.title}
                </h3>
                
                <p className="text-sm text-neutral-700 leading-relaxed font-medium">
                  {step.description}
                </p>

                {/* Arrow connector for non-last items */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute -right-3 top-12 text-brand-500 opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* CTA Section */}
      <motion.div 
        className="mt-16 text-center max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <div className="p-8 bg-gradient-to-br from-brand-500 to-brand-600 rounded-3xl shadow-glow text-white">
          <p className="text-2xl font-black mb-2">Average book time doesn't exist.</p>
          <p className="text-lg font-medium text-snow-100 mb-3">
            With snowblowrr, set it and forget it. Seeing the neighbours struggle as you drive off with a fresh unspilled coffee is enough reminder why you signed up once.
          </p>
        </div>
      </motion.div>
    </Section>
  );
}

